import React from 'react';
import styled from 'styled-components';
import { Icon, PlainButton } from 'components/v3';
import { LIMITS } from '../../shared/fieldTypes';
import { phone } from '../palette';
import { CellInput, Muted, Stack, intIn, listOf } from './styles';
import { inputPropTypes } from './TextInputs';

// Table, repeating list, matrix and rating.

const objectOf = (value) =>
  value && typeof value === 'object' && !Array.isArray(value) ? value : {};

const cellText = (v) => (typeof v === 'string' ? v : '');

const columnName = (columns, i) =>
  (typeof columns[i] === 'string' && columns[i].trim()) || `Column ${i + 1}`;

/* ---------- Table: fixed rows × columns, answers keyed "<row>-<col>" ---------- */

const TableScroll = styled.div`
  overflow-x: auto;
`;

const TableGrid = styled.div`
  display: grid;
  gap: 6px;
  min-width: 100%;
`;

const TableHead = styled.div`
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.3px;
  text-transform: uppercase;
  color: ${(p) => phone(p.theme).text3};
  padding: 2px 2px 4px;
  overflow-wrap: anywhere;
`;

export const TableInput = ({
  field,
  value,
  onChange,
  labelId,
  describedBy,
  invalid,
}) => {
  const config = field.config || {};
  const columns = listOf(config.columns);
  const rows = intIn(config.rows, LIMITS.tableRowsMin, LIMITS.tableRowsMax, 2);
  const v = objectOf(value);
  const cols = Math.max(columns.length, 1);
  const cells = [];
  for (let r = 0; r < rows; r += 1) {
    for (let c = 0; c < columns.length; c += 1) {
      const key = `${r}-${c}`;
      cells.push(
        <CellInput
          key={key}
          aria-label={`${columnName(columns, c)}, row ${r + 1}`}
          autoComplete="off"
          value={cellText(v[key])}
          onChange={(e) => onChange({ ...v, [key]: e.target.value })}
          $invalid={invalid}
        />
      );
    }
  }
  return (
    <TableScroll
      role="group"
      aria-labelledby={labelId}
      aria-describedby={describedBy}
    >
      <TableGrid
        style={{ gridTemplateColumns: `repeat(${cols}, minmax(84px, 1fr))` }}
      >
        {columns.map((_column, i) => (
          <TableHead key={`h-${i}`} aria-hidden="true">
            {columnName(columns, i)}
          </TableHead>
        ))}
        {cells}
      </TableGrid>
    </TableScroll>
  );
};
TableInput.propTypes = inputPropTypes;

/* ---------- Repeating list: one card per entry, add / remove up to max ---------- */

const EntryCard = styled.div`
  border: 1px solid ${(p) => phone(p.theme).border};
  border-radius: 12px;
  padding: 10px 12px 12px;
  background: ${(p) => phone(p.theme).surface};
`;

const EntryHead = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.5px;
  text-transform: uppercase;
  color: ${(p) => phone(p.theme).text3};
`;

const RemoveButton = styled(PlainButton)`
  display: inline-flex;
  padding: 2px;
  border-radius: 6px;
  color: ${(p) => phone(p.theme).text3};
`;

const AddButton = styled(PlainButton)`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 11px 12px;
  border-radius: 12px;
  border: 1.5px dashed ${(p) => phone(p.theme).accent};
  color: ${(p) => phone(p.theme).accent};
  font-weight: 700;
  font-size: 14px;
`;

const CenterNote = styled(Muted)`
  text-align: center;
`;

// "Crew members" → "Crew member 1".
export const entryLabel = (label, n) => {
  const base = (typeof label === 'string' && label.trim()) || 'Entry';
  return `${base.replace(/s$/, '')} ${n}`;
};

export const RepeaterInput = ({
  field,
  value,
  onChange,
  labelId,
  describedBy,
  invalid,
}) => {
  const config = field.config || {};
  const columns = listOf(config.columns);
  const max = intIn(
    config.max,
    LIMITS.repeaterMaxMin,
    LIMITS.repeaterMaxMax,
    10
  );
  const entries = Array.isArray(value) && value.length ? value : [{}];
  const setCell = (index, col, text) => {
    const next = entries.slice();
    next[index] = { ...objectOf(entries[index]), [col]: text };
    onChange(next);
  };
  const remove = (index) => onChange(entries.filter((_, i) => i !== index));
  return (
    <Stack
      $gap={10}
      role="group"
      aria-labelledby={labelId}
      aria-describedby={describedBy}
    >
      {entries.map((entry, index) => {
        const name = entryLabel(field.label, index + 1);
        const row = objectOf(entry);
        return (
          // Entries have no ids; position is their identity here.
          <EntryCard key={index}>
            <EntryHead>
              <span>{name}</span>
              {entries.length > 1 ? (
                <RemoveButton
                  aria-label={`Remove ${name}`}
                  onClick={() => remove(index)}
                >
                  <Icon name="x" size={16} />
                </RemoveButton>
              ) : null}
            </EntryHead>
            <Stack>
              {columns.map((_column, col) => (
                <CellInput
                  key={col}
                  aria-label={`${columnName(columns, col)}, ${name}`}
                  placeholder={columnName(columns, col)}
                  autoComplete="off"
                  value={cellText(row[col])}
                  onChange={(e) => setCell(index, col, e.target.value)}
                  $invalid={invalid}
                />
              ))}
            </Stack>
          </EntryCard>
        );
      })}
      {entries.length < max ? (
        <AddButton onClick={() => onChange(entries.concat([{}]))}>
          <Icon name="plus" size={16} />
          {(typeof config.add_label === 'string' && config.add_label.trim()) ||
            'Add another'}
        </AddButton>
      ) : (
        <CenterNote>Maximum of {max} reached</CenterNote>
      )}
    </Stack>
  );
};
RepeaterInput.propTypes = inputPropTypes;

/* ---------- Matrix: one choice per row, answers keyed row label → column label ---------- */

const MatrixRow = styled.div`
  display: grid;
  align-items: center;
  gap: 4px;
  margin-bottom: 4px;
`;

const MatrixColHead = styled.div`
  text-align: center;
  font-size: 10.5px;
  font-weight: 700;
  letter-spacing: 0.3px;
  text-transform: uppercase;
  color: ${(p) => phone(p.theme).text3};
  overflow-wrap: anywhere;
`;

const MatrixRowHead = styled.div`
  font-size: 13px;
  font-weight: 600;
  padding-right: 6px;
  color: ${(p) => phone(p.theme).text};
  overflow-wrap: anywhere;
`;

const MatrixCell = styled(PlainButton)`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 9px 0;
  border-radius: 8px;
  border: 1px solid
    ${(p) => {
      if (p.$on) return phone(p.theme).accent;
      return p.$invalid ? phone(p.theme).error : phone(p.theme).border;
    }};
  background: ${(p) =>
    p.$on ? phone(p.theme).accentSoft : phone(p.theme).surface};

  span {
    width: 16px;
    height: 16px;
    box-sizing: border-box;
    border-radius: 50%;
    border: 2px solid
      ${(p) => (p.$on ? phone(p.theme).accent : phone(p.theme).borderStrong)};
    background: ${(p) => (p.$on ? phone(p.theme).accent : 'transparent')};
    box-shadow: ${(p) =>
      p.$on ? `inset 0 0 0 2px ${phone(p.theme).chrome}` : 'none'};
  }
`;

export const MatrixInput = ({
  field,
  value,
  onChange,
  labelId,
  describedBy,
  invalid,
}) => {
  const config = field.config || {};
  const rows = listOf(config.rows);
  const cols = listOf(config.cols);
  const v = objectOf(value);
  const template = {
    gridTemplateColumns: `1.3fr repeat(${Math.max(cols.length, 1)}, 1fr)`,
  };
  return (
    <div role="group" aria-labelledby={labelId} aria-describedby={describedBy}>
      <MatrixRow style={template} aria-hidden="true">
        <div />
        {cols.map((col, i) => (
          <MatrixColHead key={i}>{col}</MatrixColHead>
        ))}
      </MatrixRow>
      {rows.map((row, r) => (
        <MatrixRow key={r} style={template} role="radiogroup" aria-label={row}>
          <MatrixRowHead aria-hidden="true">{row}</MatrixRowHead>
          {cols.map((col, i) => {
            const on = v[row] === col;
            return (
              <MatrixCell
                key={i}
                role="radio"
                aria-checked={on}
                aria-label={col}
                $on={on}
                $invalid={invalid && !v[row]}
                onClick={() => onChange({ ...v, [row]: col })}
              >
                <span />
              </MatrixCell>
            );
          })}
        </MatrixRow>
      ))}
    </div>
  );
};
MatrixInput.propTypes = inputPropTypes;

/* ---------- Rating: 1..max stars ---------- */

const Stars = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const Star = styled(PlainButton)`
  display: inline-flex;
  border-radius: 6px;
  color: ${(p) =>
    p.$lit ? phone(p.theme).accent : phone(p.theme).borderStrong};

  svg {
    fill: ${(p) => (p.$lit ? phone(p.theme).accent : 'none')};
  }
`;

export const RatingInput = ({
  field,
  value,
  onChange,
  labelId,
  describedBy,
}) => {
  const max = intIn(
    field.config && field.config.max,
    LIMITS.ratingMaxMin,
    LIMITS.ratingMaxMax,
    5
  );
  const current = Number.isInteger(value) ? value : 0;
  const stars = [];
  for (let n = 1; n <= max; n += 1) {
    stars.push(
      <Star
        key={n}
        aria-label={`${n} ${n === 1 ? 'star' : 'stars'}`}
        aria-pressed={current === n}
        $lit={current >= n}
        onClick={() => onChange(n)}
      >
        <Icon name="star" size={30} />
      </Star>
    );
  }
  return (
    <Stars
      role="group"
      aria-labelledby={labelId}
      aria-describedby={describedBy}
    >
      {stars}
    </Stars>
  );
};
RatingInput.propTypes = inputPropTypes;
